import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { notificationsAPI } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      viewAsRole: null,

      login: async (email, password) => {
        let response;
        try {
          response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: (email || '').trim().toLowerCase(), password }),
          });
        } catch (e) {
          throw new Error('No se ha podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.');
        }

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Email o contraseña incorrectos. Revisa que el navegador no haya autocompletado otra contraseña.');
          }
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || `Error al iniciar sesión (${response.status}). Inténtalo de nuevo en unos segundos.`);
        }
        
        const data = await response.json();
        set({ 
          user: data.user, 
          token: data.token, 
          isAuthenticated: true,
          viewAsRole: data.user.role
        });
        return data;
      },

      register: async (userData) => {
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userData),
        });
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Error al registrarse' }));
          throw new Error(error.detail || 'Error al registrarse');
        }
        
        return response.json();
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, viewAsRole: null, impersonation: null });
      },

      // "Ver el portal como…" (solo CEO): guarda la sesión original y entra con un token temporal del miembro
      impersonation: null, // { originalUser, originalToken, since }
      impersonate: async (userId) => {
        const { token, user } = get();
        const res = await fetch(`${API_URL}/auth/impersonate/${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'No se pudo entrar como ese miembro');
        }
        const data = await res.json();
        set({
          impersonation: { originalUser: user, originalToken: token, since: new Date().toISOString() },
          user: data.user,
          token: data.token,
          isAuthenticated: true,
          viewAsRole: data.user.role,
        });
        return data.user;
      },
      viewClientPortal: async (clientId) => {
        const { token, user } = get();
        const res = await fetch(`${API_URL}/auth/view-client/${clientId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'No se pudo abrir el portal del cliente');
        }
        const data = await res.json();
        set({
          impersonation: { originalUser: user, originalToken: token, since: new Date().toISOString(), clientView: true },
          user: data.user, token: data.token, isAuthenticated: true, viewAsRole: data.user.role,
        });
        return data.user;
      },
      stopImpersonating: () => {
        const imp = get().impersonation;
        if (!imp) return;
        set({ user: imp.originalUser, token: imp.originalToken, isAuthenticated: true, viewAsRole: imp.originalUser?.role || 'ceo', impersonation: null });
      },

      setViewAsRole: (role) => {
        set({ viewAsRole: role });
      },

      getHeaders: () => {
        const token = get().token;
        return {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        };
      },
    }),
    {
      name: 'karma-auth',
    }
  )
);

// Notifications Store
export const useNotificationStore = create((set, get) => ({
  notifications: [],
  loading: false,

  fetchNotifications: async () => {
    try {
      const data = await notificationsAPI.getAll();
      set({ notifications: Array.isArray(data) ? data : [] });
    } catch (e) {
      // silencioso: si no hay sesión o falla, no rompemos la UI
    }
  },

  get unreadCount() {
    return get().notifications.filter((n) => !n.read).length;
  },

  markAsRead: async (id) => {
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
    }));
    try { await notificationsAPI.markAsRead(id); } catch (e) {}
  },

  markAllAsRead: async () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
    }));
    try { await notificationsAPI.markAllAsRead(); } catch (e) {}
  },

  clearNotification: async (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
    try { await notificationsAPI.remove(id); } catch (e) {}
  },

  clearAll: () => set({ notifications: [] }),
}));

// UI Store
export const useUIStore = create((set) => ({
  sidebarCollapsed: false,
  currentSection: 'dashboard',
  
  toggleSidebar: () => set(state => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setCurrentSection: (section) => set({ currentSection: section }),
}));
